import type { CSSProperties } from "react";

import { atomWithStorage } from "jotai/utils";

/**
 * Fond d'écran du bureau : le thème (aucun fond), une IMAGE de la collection
 * livrée avec le cockpit, ou une image importée par la personne.
 *
 * 🪦 Les dégradés de couleur (graphite, océan, crépuscule…) ont été retirés le
 * 07-10-2026 — Kayla : « actuellement, c'est des couleurs. J'aimerais bien
 * supprimer ces couleurs-là », remplacées par une collection d'images. Un
 * dégradé encore posé dans un navigateur retombe sur le thème (cf `wallpaperBackground`).
 *
 * On stocke l'objet complet (pas juste un id) pour que les images importées
 * survivent et que la collection puisse évoluer sans casser les choix passés.
 */
export type WallpaperKind = "theme" | "gradient" | "solid" | "image";

export interface Wallpaper {
  /** Id stable : id de la collection, ou "custom" pour une image importée. */
  id: string;
  kind: WallpaperKind;
  /** URL / data-URL de l'image. "" = thème. */
  value: string;
  /** Clé i18n du libellé (collection uniquement). */
  labelKey?: string;
  /** Petite version de l'image, pour la grille de choix (collection). */
  vignette?: string;
  /** Nom lisible (collection), quand il n'y a pas de clé i18n. */
  nom?: string;
}

/** Défaut : transparent → laisse transparaître le fond de panneau (thème). */
export const DEFAULT_WALLPAPER: Wallpaper = {
  id: "theme",
  kind: "theme",
  value: "",
  labelKey: "shell.explorer.wallpaper.presets.theme",
};

/**
 * La collection d'images livrée avec le cockpit — `public/fonds/<nom>.jpg`
 * (1920 px, le fond) et `public/fonds/mini/<nom>.jpg` (la vignette de la
 * grille). Ajouter une image = déposer les deux fichiers et ajouter son nom
 * ici. Les fonds sont plutôt sombres.
 */
export const FONDS_D_ECRAN: readonly string[] = [
  "cascade",
  "neon",
  "sommet",
  "lune",
  "paillettes",
  "bureau-studio",
  "neige",
  "bureau-jour",
  "collines",
  "palmiers",
];

export const WALLPAPER_PRESETS: Wallpaper[] = [
  DEFAULT_WALLPAPER,
  ...FONDS_D_ECRAN.map(
    (nom): Wallpaper => ({
      id: `fond:${nom}`,
      kind: "image",
      value: `/fonds/${nom}.jpg`,
      vignette: `/fonds/mini/${nom}.jpg`,
      nom: nom.replace(/-/g, " "),
    }),
  ),
];

/** Fond d'écran courant (persisté global utilisateur). */
export const wallpaperAtom = atomWithStorage<Wallpaper>(
  "cockpit:wallpaper:v1",
  DEFAULT_WALLPAPER,
);

/**
 * Valeur CSS `background` à appliquer (au canvas, ou à un swatch d'aperçu).
 * `undefined` pour le thème (transparent → fond de panneau).
 */
export function wallpaperBackground(w: Wallpaper): string | undefined {
  if (w.kind !== "image" || !w.value) return undefined;
  const plein = `center / cover no-repeat url("${w.value}")`;
  // ⚡ La vignette SOUS l'image : elle est déjà dans le cache (la grille
  // l'affiche), donc le fond change AU CLIC, flou une fraction de seconde,
  // puis net dès que l'image pleine arrive. Sans elle, un premier choix
  // attendait le téléchargement (150-550 Ko) — Kayla, 07-10-2026 : « quand
  // je change un fond d'écran, il ne se change pas instantanément ».
  return w.vignette ? `${plein}, center / cover no-repeat url("${w.vignette}")` : plein;
}

/** Télécharge d'avance les fonds de la collection (appelé à l'ouverture de la
 *  grille) : au clic, l'image pleine est le plus souvent déjà là. */
export function prechargerLesFonds(): void {
  if (typeof Image === "undefined") return;
  for (const w of WALLPAPER_PRESETS) {
    if (w.kind === "image" && w.value) new Image().src = w.value;
  }
}

/** Style inline du canvas du bureau. */
export function wallpaperStyle(w: Wallpaper): CSSProperties {
  const background = wallpaperBackground(w);
  return background ? { background } : {};
}

/**
 * Vrai si un vrai fond est posé (≠ thème) : les libellés d'icônes doivent alors
 * passer en blanc + ombre pour rester lisibles par-dessus.
 */
export function wallpaperHasBackdrop(w: Wallpaper): boolean {
  return w.kind === "image" && Boolean(w.value);
}

/**
 * Convertit une image importée en `Wallpaper` (kind "image") : on la
 * redimensionne (max `maxDim` px sur le grand côté) et on la ré-encode en JPEG
 * côté client → data-URL compacte qui tient dans le localStorage (pas de
 * storage backend). Le fond est persisté tel quel dans `wallpaperAtom`.
 */
export async function imageFileToWallpaper(
  file: File,
  maxDim = 1600,
  quality = 0.8,
): Promise<Wallpaper> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(file);
  });

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("decode failed"));
    i.src = dataUrl;
  });

  const scale = Math.min(1, maxDim / Math.max(img.width, img.height || 1));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return { id: "custom", kind: "image", value: dataUrl };
  ctx.drawImage(img, 0, 0, w, h);
  return { id: "custom", kind: "image", value: canvas.toDataURL("image/jpeg", quality) };
}
